import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faUser, faGraduationCap, faBriefcase } from '@fortawesome/free-solid-svg-icons';
import './ReviewerManagement.css';
import { API_BASE_URL } from '../config/api';

const ReviewerManagement = () => {
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingReviewer, setEditingReviewer] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    graduation: ''
  });

  useEffect(() => {
    // Load reviewers data
    loadReviewers();
  }, []);

  const loadReviewers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/reviewers`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setReviewers(data);
    } catch (err) {
      setError('Failed to load reviewers');
      console.error('Error loading reviewers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editingReviewer) {
        // Update existing reviewer
        const response = await fetch(`${API_BASE_URL}/reviewers/${editingReviewer.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update reviewer');
        }
        
        // Reload reviewers to get updated data
        await loadReviewers();
      } else {
        // Create new reviewer
        const response = await fetch(`${API_BASE_URL}/reviewers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create reviewer');
        }
        
        // Reload reviewers to get updated data
        await loadReviewers();
      }

      // Reset form
      setFormData({ name: '', designation: '', graduation: '' });
      setShowForm(false);
      setEditingReviewer(null);
    } catch (err) {
      setError('Failed to save reviewer');
      console.error('Error saving reviewer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (reviewer) => {
    setFormData({
      name: reviewer.name,
      designation: reviewer.designation,
      graduation: reviewer.graduation
    });
    setEditingReviewer(reviewer);
    setShowForm(true);
  };

  const handleDelete = async (reviewerId) => {
    if (!window.confirm('Are you sure you want to delete this reviewer?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/reviewers/${reviewerId}`, { 
        method: 'DELETE' 
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete reviewer');
      }
      
      // Reload reviewers to get updated data
      await loadReviewers();
    } catch (err) {
      setError(err.message || 'Failed to delete reviewer');
      console.error('Error deleting reviewer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', designation: '', graduation: '' });
    setShowForm(false);
    setEditingReviewer(null);
  };

  return (
    <Container fluid className="reviewer-management">
      {/* Header Section */}
      <Row className="mb-4">
        <Col xs={12} md={8}>
          <h2 className="page-title">
            <FontAwesomeIcon icon={faUser} className="me-2" />
            Reviewer Management
          </h2>
        </Col>
        <Col xs={12} md={4} className="text-md-end">
          <Button 
            variant="success" 
            size="lg"
            onClick={() => setShowForm(true)}
            className="w-100 w-md-auto"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add New Reviewer
          </Button>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Form Modal */}
      <Modal show={showForm} onHide={handleCancel} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editingReviewer ? faEdit : faPlus} className="me-2" />
            {editingReviewer ? 'Edit Reviewer' : 'Add New Reviewer'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FontAwesomeIcon icon={faUser} className="me-1" />
                    Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter reviewer name"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FontAwesomeIcon icon={faBriefcase} className="me-1" />
                    Designation *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Quality Manager, Senior Reviewer"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FontAwesomeIcon icon={faGraduationCap} className="me-1" />
                    Graduation *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="graduation"
                    value={formData.graduation}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., B.Tech (Civil), M.Tech (Structural)"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={editingReviewer ? faEdit : faPlus} className="me-2" />
                {editingReviewer ? 'Update' : 'Save'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reviewers List */}
      <Row>
        <Col>
          <Card className="reviewers-card">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Reviewers List
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && reviewers.length === 0 ? (
                <div className="text-center p-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading reviewers...</p>
                </div>
              ) : reviewers.length === 0 ? (
                <div className="text-center p-4">
                  <FontAwesomeIcon icon={faUser} size="3x" className="text-muted mb-3" />
                  <p className="text-muted">No reviewers found.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="d-none d-lg-block">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th>Designation</th>
                            <th>Graduation</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reviewers.map(reviewer => (
                            <tr key={reviewer.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                                  {reviewer.name}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FontAwesomeIcon icon={faBriefcase} className="me-2 text-muted" />
                                  {reviewer.designation}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <FontAwesomeIcon icon={faGraduationCap} className="me-2 text-muted" />
                                  {reviewer.graduation}
                                </div>
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEdit(reviewer)}
                                  disabled={loading}
                                  className="me-2"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(reviewer.id)}
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  {/* Mobile Card View */}
                  <div className="d-lg-none">
                    {reviewers.map(reviewer => (
                      <Card key={reviewer.id} className="border-0 border-bottom rounded-0">
                        <Card.Body>
                          <Row>
                            <Col xs={12}>
                              <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                                <strong>{reviewer.name}</strong>
                              </div>
                              <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon icon={faBriefcase} className="me-2 text-muted" />
                                <span className="text-muted">{reviewer.designation}</span>
                              </div>
                              <div className="d-flex align-items-center mb-3">
                                <FontAwesomeIcon icon={faGraduationCap} className="me-2 text-muted" />
                                <span className="text-muted">{reviewer.graduation}</span>
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEdit(reviewer)}
                                  disabled={loading}
                                  className="flex-fill"
                                >
                                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(reviewer.id)}
                                  disabled={loading}
                                  className="flex-fill"
                                >
                                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                                  Delete
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ReviewerManagement;
